import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { IconButton, Tooltip } from "@mui/material";
import { Check, Close, DeleteOutline, EditOutlined, GridView, ViewList } from "@mui/icons-material";
import Navbar from "../Navbar/navbar";
import { useAuth } from "../Login/AuthContext";
import { listWorksheets, deleteWorksheet, renameWorksheet } from "../../data/worksheetsApi";
import { TEMPLATE_NAMES } from "../Layout/Layout.jsx";
import "./MyWorksheets.css";

const VIEW_MODE_KEY = "worksheetsViewMode";

const formatDate = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const MyWorksheets = () => {
  const { user } = useAuth();
  const [worksheets, setWorksheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem(VIEW_MODE_KEY) || "cards");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);
  const [renameError, setRenameError] = useState('');

  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data } = await listWorksheets(user.id);
      if (!cancelled) {
        setWorksheets(data);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem(VIEW_MODE_KEY, mode);
  };

  const handleDeleteClick = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteError('');
    setConfirmDeleteId(id);
  };

  const handleCancelDelete = () => {
    setDeleteError('');
    setConfirmDeleteId(null);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteId) return;
    setDeleting(true);
    setDeleteError('');
    const { error } = await deleteWorksheet(confirmDeleteId);
    if (!error) {
      setWorksheets((prev) => prev.filter((ws) => ws.id !== confirmDeleteId));
      setConfirmDeleteId(null);
    } else {
      setDeleteError('Could not delete this worksheet. Please try again.');
    }
    setDeleting(false);
  };

  const handleRenameClick = (e, ws) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingId(ws.id);
    setEditingTitle(ws.title);
    setRenameError('');
  };

  const handleRenameCancel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingId(null);
    setEditingTitle('');
    setRenameError('');
  };

  const handleRenameSave = async (e, ws) => {
    e.preventDefault();
    e.stopPropagation();
    const trimmed = editingTitle.trim();
    if (!trimmed) {
      setRenameError('Title cannot be empty.');
      return;
    }
    if (trimmed === ws.title) {
      setEditingId(null);
      return;
    }
    setSavingTitle(true);
    setRenameError('');
    const { error } = await renameWorksheet(ws.id, trimmed);
    if (!error) {
      setWorksheets((prev) => prev.map((w) => (w.id === ws.id ? { ...w, title: trimmed } : w)));
      setEditingId(null);
    } else {
      setRenameError('Could not rename this worksheet. Please try again.');
    }
    setSavingTitle(false);
  };

  return (
    <div className="my-worksheets-body">
      <Navbar />

      <div className="my-worksheets-content">
        <div className="my-worksheets-header">
          <h1 className="my-worksheets-title">My Worksheets</h1>

          {!loading && worksheets.length > 0 && (
            <div className="view-toggle">
              <Tooltip title="Card view">
                <IconButton
                  size="small"
                  className={`view-toggle-btn${viewMode === "cards" ? " active" : ""}`}
                  onClick={() => handleViewModeChange("cards")}
                  aria-label="Card view"
                >
                  <GridView fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="List view">
                <IconButton
                  size="small"
                  className={`view-toggle-btn${viewMode === "list" ? " active" : ""}`}
                  onClick={() => handleViewModeChange("list")}
                  aria-label="List view"
                >
                  <ViewList fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          )}
        </div>

        {loading && <p className="my-worksheets-status">Loading your worksheets…</p>}

        {!loading && worksheets.length === 0 && (
          <p className="my-worksheets-status">
            You haven't saved any worksheets yet — export one to save it here.
          </p>
        )}

        {!loading && worksheets.length > 0 && (
          <div className={viewMode === "cards" ? "my-worksheets-grid" : "my-worksheets-list"}>
            {worksheets.map((ws) => (
              <Link
                key={ws.id}
                to={`/worksheet/${ws.id}`}
                className={`worksheet-item worksheet-item--${viewMode === "cards" ? "card" : "list"}`}
                onClick={(e) => { if (editingId === ws.id) e.preventDefault(); }}
              >
                <div className="worksheet-item-content">
                  {editingId === ws.id ? (
                    <div className="worksheet-title-edit" onClick={(e) => e.stopPropagation()}>
                      <div className="worksheet-title-edit-row">
                        <input
                          className="worksheet-title-input"
                          value={editingTitle}
                          onChange={(e) => setEditingTitle(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleRenameSave(e, ws);
                            if (e.key === 'Escape') handleRenameCancel(e);
                          }}
                          disabled={savingTitle}
                          autoFocus
                        />
                        <IconButton
                          className="worksheet-title-save-btn"
                          size="small"
                          aria-label="Save name"
                          onClick={(e) => handleRenameSave(e, ws)}
                          disabled={savingTitle}
                        >
                          <Check fontSize="small" />
                        </IconButton>
                        <IconButton
                          className="worksheet-title-cancel-btn"
                          size="small"
                          aria-label="Cancel rename"
                          onClick={handleRenameCancel}
                          disabled={savingTitle}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </div>
                      {renameError && <p className="worksheet-title-error">{renameError}</p>}
                    </div>
                  ) : (
                    <p
                      className="worksheet-item-title worksheet-item-title--editable"
                      onClick={(e) => handleRenameClick(e, ws)}
                      title="Click to rename"
                    >
                      {ws.title}
                    </p>
                  )}
                  <p className="worksheet-item-template">
                    {TEMPLATE_NAMES[ws.template_id] || ws.template_id}
                  </p>
                  <p className="worksheet-item-date">
                    Last edited {formatDate(ws.updated_at)}
                  </p>
                </div>
                {editingId !== ws.id && (
                  <div className="worksheet-item-icons">
                    <Tooltip title="Edit worksheet">
                      <IconButton
                        className="worksheet-rename-btn"
                        size="small"
                        aria-label="Edit worksheet"
                      >
                        <EditOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete worksheet">
                      <IconButton
                        className="worksheet-delete-btn"
                        size="small"
                        aria-label="Delete worksheet"
                        onClick={(e) => handleDeleteClick(e, ws.id)}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>

      {confirmDeleteId && (
        <div className="delete-confirm-overlay">
          <div className="delete-confirm-modal">
            <p className="delete-confirm-title">Delete this worksheet?</p>
            <p className="delete-confirm-body">This cannot be undone.</p>
            {deleteError && <p className="delete-confirm-error">{deleteError}</p>}
            <div className="delete-confirm-actions">
              <button
                className="modal-btn modal-btn-cancel"
                onClick={handleCancelDelete}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="modal-btn modal-btn-delete"
                onClick={handleConfirmDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyWorksheets;
